import type { ReactNode } from 'react';

import { BlurredAppSidebar, ShellFrame, type RoomStatus } from '@/components/rooms';

/** The signed-in operator as the sidebar's account row shows them: only what the login session gave. */
export interface ShellOperator {
  /** The name the login returned (the BFF answers the e-mail when the account has no other name). */
  name?: string;
  email?: string;
  /** The label of their role (see `roleLabelFromGroups`); absent when none of their groups has one. */
  role?: string;
}

export interface OnboardingShellProps {
  /** The prospect's brand, once known: the sidebar's brand switcher shows it (before a website it shows TP's own fallback). */
  brandName?: string;
  /** The signed-in operator: the sidebar's account row. */
  operator?: ShellOperator;
  /** How far the pinned Onboarding room is; the sidebar dot is grey while open, green once completed. */
  roomStatus?: RoomStatus;
  /** The Tasks panel (TP: `aside w-[340px] hidden lg:block`), beside the chat inside the inset panel. Not drawn without it (the login gate). */
  aside?: ReactNode;
  children: ReactNode;
}

/**
 * The page frame the onboarding lives in, as TP's /onboarding-v2 draws it: a blurred inert rail and, beside
 * it, the inset panel holding the chat. There is no page header: the conversation starts at the top.
 */
export function OnboardingShell({ brandName, operator, roomStatus = 'open', aside, children }: OnboardingShellProps) {
  return (
    <ShellFrame
      sidebar={
        <BlurredAppSidebar
          brandName={brandName}
          userName={operator?.name}
          userEmail={operator?.email}
          userRole={operator?.role}
          roomStatus={roomStatus}
        />
      }
    >
      {/* The chat's box: the column stays 760px and centred in what the aside leaves. */}
      <div data-testid="chat-area" className="flex min-h-0 min-w-0 flex-1 flex-col">{children}</div>
      {aside ? (
        <aside data-testid="tasks-aside" className="hidden w-[340px] shrink-0 overflow-y-auto p-3 lg:block">
          {aside}
        </aside>
      ) : null}
    </ShellFrame>
  );
}
